import type { ComponentPropsWithoutRef, ElementRef } from "react"
import { forwardRef } from "react"
import * as HoverCardPrimitive from "@radix-ui/react-hover-card"

import { cn } from "@productlane/lib/src/cn"

/**
 * For sighted users to preview content available behind a link.
 *
 * @example
 * ```tsx
 * import { HoverCard, HoverCardTrigger, HoverCardContent } from './hover-card';
 *
 * function App() {
 *   return (
 *     <HoverCard>
 *       <HoverCardTrigger>Hover</HoverCardTrigger>
 *       <HoverCardContent>
 *         The React Framework – created and maintained by @vercel.
 *       </HoverCardContent>
 *     </HoverCard>
 *   );
 * }
 * ```
 */
const HoverCard = HoverCardPrimitive.Root

const HoverCardTrigger = HoverCardPrimitive.Trigger

const HoverCardPortal = HoverCardPrimitive.Portal

const HoverCardContent = forwardRef<
	ElementRef<typeof HoverCardPrimitive.Content>,
	ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => (
	<HoverCardPrimitive.Content
		ref={ref}
		align={align}
		sideOffset={sideOffset}
		className={cn(
			"z-50 w-64 rounded-md border border-gray-200 bg-white p-4 shadow-md outline-none ring-offset-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:border-gray-700/40 dark:bg-gray-850 dark:text-white dark:ring-offset-gray-900",
			className,
		)}
		{...props}
	/>
))

export { HoverCard, HoverCardTrigger, HoverCardContent, HoverCardPortal }
